"use client";
import { Category } from "@/dto/category";
import { Merchant } from "@/dto/merchant";
import { lightenColor } from "@/utils/lightenColor";
import { useCategoryInView } from "@/utils/useCategoryInView";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Dispatch, SetStateAction } from "react";
import ReactModal from "react-modal";
import CloseIcon from "../_icons/closeIcon";
import { TFunction } from "@/types";
import { UNCATEGORISED_CATEGORY_ID } from "@/utils/constants";
import { filterRestaurantCategories } from "@/utils/itemUtils";
import { decodeHtmlEntities } from "@/utils/htmlEntityUtils";

export interface MobileCategoryModalProps {
  menu: Category[];
  setIsCategoryMenuOpen: Dispatch<SetStateAction<boolean | undefined>>;
  isCategoryMenuOpen?: boolean;
  serviceCategories: Category[];
  retailCategories: Category[];
  merchant: Merchant;
}

export const MobileCategoryModal = ({
  menu,
  setIsCategoryMenuOpen,
  isCategoryMenuOpen,
  merchant,
  serviceCategories,
  retailCategories,
}: MobileCategoryModalProps) => {
  const path = usePathname();
  const t = useTranslations("MobileCategoryModal");
  const isHybridService = merchant?.isHybrid && path?.includes("services");
  const isHybridShop =
    (merchant?.isHybrid ||
      (merchant.isService && merchant.hasOnlyRetailItems)) &&
    path?.includes("shop");
  const getTitle = () => {
    if (merchant?.isRestaurant || isHybridShop) {
      return t("categories");
    }
    if (merchant?.isService || isHybridService) {
      return t("services");
    }
  };

  const getCategories = () => {
    if (merchant?.isRestaurant) {
      const availableMenuCategories = filterRestaurantCategories(menu);
      return availableMenuCategories;
    }
    if (isHybridShop) {
      return retailCategories;
    }
    if (merchant?.isService || isHybridService) {
      return serviceCategories;
    }

    return menu;
  };

  const title = getTitle();
  const categories = getCategories();

  return (
    <ReactModal
      shouldCloseOnOverlayClick={true}
      appElement={
        globalThis.window === undefined
          ? undefined
          : document.getElementById("app") || undefined
      }
      overlayClassName={"fixed inset-0 bg-[#25333880] z-50"}
      className={"fixed bottom-0 w-full z-50"}
      isOpen={isCategoryMenuOpen ?? false}
      onRequestClose={() => setIsCategoryMenuOpen(false)}
      closeTimeoutMS={500}
    >
      <div className="bg-white w-full pt-[14px] pb-6 rounded-t-lg ">
        <div className="flex justify-center items-center shadow-categoryModal pb-[14px] px-4">
          <span
            className={"text-sm leading-6 font-semibold flex-grow text-center"}
          >
            {title}
          </span>
          <button
            onClick={() => setIsCategoryMenuOpen(false)}
            aria-label={t("close")}
          >
            <CloseIcon />
          </button>
        </div>
        <Categories
          menu={categories}
          merchant={merchant}
          setIsCategoryMenuOpen={setIsCategoryMenuOpen}
          t={t}
        />
      </div>
    </ReactModal>
  );
};

const Categories = ({
  menu,
  merchant,
  setIsCategoryMenuOpen,
  t,
}: {
  menu: Category[];
  merchant: Merchant;
  setIsCategoryMenuOpen: Dispatch<SetStateAction<boolean | undefined>>;
  t: TFunction;
}) => {
  const [categoryIdInView] = useCategoryInView();
  const merchantColor = merchant?.merchantMeta?.colorPalette?.color;
  const lighterMerchantColor = lightenColor(merchantColor, 90);
  const activeStyle = {
    color: merchantColor,
    backgroundColor: lighterMerchantColor,
  };
  return (
    <div className="flex flex-col max-h-[91vh] overflow-y-auto">
      {menu.map((menuItem: any) => {
        const uncategorizedItemCondition =
          menuItem?.id === UNCATEGORISED_CATEGORY_ID;
        const uri = uncategorizedItemCondition
          ? t("uncategorized")
          : menuItem.name;
        return (
          <Link
            key={menuItem.id}
            style={
              (uncategorizedItemCondition ? "Uncategorized" : menuItem.name) ===
              categoryIdInView
                ? activeStyle
                : {}
            }
            className={`py-3 px-4 font-[#373737] text-[15px] leading-6 border-b border-b-[#EDEDED]`}
            href={`#${encodeURIComponent(uri)}`}
            onClick={() => setIsCategoryMenuOpen(false)}
          >
            {decodeHtmlEntities(menuItem.name)}
          </Link>
        );
      })}
    </div>
  );
};
